import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  useTheme,
  Divider,
  Button,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircleOutline as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Image as ImageIcon,
  DownloadRounded,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";
import PIDMarkup from "../../../../components/PIDMarkup/PIDMarkup";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const useStyles = makeStyles()((theme) => ({
  card: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    background: theme.palette.background.paper,
  },
  headerSection: {
    background:
      theme.palette.mode === "dark"
        ? theme.palette.background.dark
        : theme.palette.background.light,
    borderRadius: "12px 12px 0 0",
    padding: theme.spacing(2),
  },
  stepRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.02)",
    },
  },
  tableCell: {
    fontSize: "0.875rem",
  },
  actionChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  thumbnailButton: {
    padding: theme.spacing(0.5),
  },
  truncateText: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

function IsolationChecklistBlock({
  answer,
  block,
  isStreaming = false,
  isolationMarkup,
  onIsolationMarkupChange,
  selectedIsolationMarkup,
  onSelectedIsolationMarkupChange,
  lastStreamedMessageId,
}) {
  const { classes } = useStyles();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const [openPIDMarkup, setOpenPIDMarkup] = useState(false);

  // Queue system for handling step updates during streaming
  const updateQueueRef = useRef([]);
  const isProcessingRef = useRef(false);
  const pendingTimeoutsRef = useRef([]);

  const { attributes, content } = block;

  // Helper function to safely extract text from nested XML nodes
  const getNodeText = (node, key) => {
    if (!node || !node[key]) return "";
    const value = node[key];
    if (typeof value === "string") return value;
    if (value["#text"]) return value["#text"];
    return "";
  };

  // Parse steps from the content
  const parseSteps = () => {
    if (!content || !content.Steps) return [];

    const stepsNode = content.Steps;
    let stepArray = [];

    if (stepsNode.Step) {
      // If Step is an array, use it directly; otherwise wrap single step in array
      stepArray = Array.isArray(stepsNode.Step)
        ? stepsNode.Step
        : [stepsNode.Step];
    }

    return stepArray.map((step) => {
      // Extract attributes (prefixed with @)
      const stepAttrs = {};
      Object.keys(step || {}).forEach((key) => {
        if (key.startsWith("@")) {
          stepAttrs[key.slice(1)] = step[key];
        }
      });

      return {
        number: stepAttrs.number || "",
        x: stepAttrs.x || "",
        y: stepAttrs.y || "",
        markup_image_url: stepAttrs.markup_image_url || "",
        description: getNodeText(step, "Description"),
        lineRef: getNodeText(step, "LineRef"),
        action: getNodeText(step, "Action"),
        purpose: getNodeText(step, "Purpose"),
        responsibility: getNodeText(step, "Responsibility"),
      };
    });
  };

  const steps = useMemo(() => parseSteps(), [content]);

  // Process next update from queue
  const processNextUpdate = () => {
    if (isProcessingRef.current || updateQueueRef.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;
    const { pid_url, annotations } = updateQueueRef.current.shift();

    // Set the PID markups
    onIsolationMarkupChange({
      pid_url,
      annotations,
    });

    // Wait before processing next update (500ms delay for streaming)
    const timeoutId = setTimeout(() => {
      pendingTimeoutsRef.current = pendingTimeoutsRef.current.filter(
        (id) => id !== timeoutId
      );
      isProcessingRef.current = false;
      processNextUpdate();
    }, 800);

    pendingTimeoutsRef.current.push(timeoutId);
  };

  // useEffect(() => {
  //   if (isStreaming && steps.length > 0) {
  //     // Add update to queue
  //     updateQueueRef.current.push({
  //       pid_url: attributes?.pid_url,
  //       annotations: steps,
  //     });
  //     // Try to process (will wait if already processing)
  //     processNextUpdate();
  //   }
  // }, [steps.length]);

  // // Cleanup all pending timeouts on unmount
  // useEffect(() => {
  //   return () => {
  //     pendingTimeoutsRef.current.forEach((timeoutId) => {
  //       clearTimeout(timeoutId);
  //     });
  //     pendingTimeoutsRef.current = [];
  //   };
  // }, []);

  // useEffect(() => {
  //   if (!isStreaming && answer?.["question_id"] === lastStreamedMessageId) {
  //     setSelectedPIDMarkups({
  //       pid_url: attributes?.pid_url,
  //       annotations: steps,
  //     });
  //   }
  // }, [steps.length, isStreaming, lastStreamedMessageId]);

  // useEffect(() => {
  //   if (!selectedPIDMarkups) {
  //     setSelectedPIDMarkups({
  //       pid_url: attributes?.pid_url,
  //       annotations: [],
  //     });
  //   }
  // }, [isStreaming]);

  // Get action color
  const getActionColor = (action) => {
    const actionUpper = action.toUpperCase();
    switch (actionUpper) {
      case "CLOSE":
        return "error";
      case "OPEN":
        return "success";
      case "LOCK":
        return "warning";
      default:
        return "default";
    }
  };

  const handleOpenPIDMarkup = () => {
    setOpenPIDMarkup(true);
  };

  const handleClosePIDMarkup = () => {
    setOpenPIDMarkup(false);
  };

  const handleClickStep = (step) => {
    onSelectedIsolationMarkupChange(step);
  };

  const handleDownloadIsolationChecklist = () => {
    const doc = new jsPDF();

    let yPosition = 15;

    // Add header table with logo, instrument name, and doc number
    autoTable(doc, {
      body: [
        [
          "LOGO",
          attributes.equipment || "Instrument Name",
          `Doc No: ${
            attributes.doc_number || attributes.version
              ? `v${attributes.version}`
              : "N/A"
          }`,
        ],
      ],
      startY: yPosition,
      theme: "plain",
      styles: {
        fontSize: 12,
        cellPadding: 4,
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
        halign: "center",
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 100, fontStyle: "bold", fontSize: 14 },
        2: { cellWidth: 50 },
      },
    });

    // Update yPosition after header table
    yPosition = doc.lastAutoTable.finalY + 10;

    // Prepare table data
    const tableData = steps.map((step) => [
      step.number,
      step.description,
      step.lineRef,
      step.action,
      step.purpose,
      step.responsibility,
    ]);

    // Add main checklist table
    autoTable(doc, {
      head: [
        [
          "Step",
          "Description",
          "Line Reference",
          "Action",
          "Purpose",
          "Responsibility",
        ],
      ],
      body: tableData,
      startY: yPosition,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        fillColor: [255, 255, 255],
      },
      headStyles: {
        fontStyle: "bold",
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 35 },
        2: { cellWidth: 25 },
        3: { cellWidth: 20 },
        4: { cellWidth: 35 },
        5: { cellWidth: 35 },
      },
    });

    // Generate filename
    const filename = `isolation_checklist_${
      attributes.equipment || "document"
    }_${new Date().toISOString().split("T")[0]}.pdf`;

    // Download PDF
    doc.save(filename);
  };

  return (
    <Box mb={2}>
      <Card className={classes.card} elevation={2}>
        {/* Header Section */}
        <Box className={classes.headerSection}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            <Box display="flex" alignItems="center" gap={1.5}>
              <AssignmentIcon
                sx={{
                  color: theme.palette.accent,
                }}
              />
              <Box>
                <Typography fontWeight={600} gutterBottom>
                  Isolation Checklist
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {attributes.equipment && (
                    <Chip
                      label={`Equipment: ${attributes.equipment}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {attributes.unit && (
                    <Chip
                      label={`Unit: ${attributes.unit}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {attributes.version && (
                    <Chip
                      label={`v${attributes.version}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {attributes.created_at && (
                    <Chip
                      label={new Date(
                        attributes.created_at
                      ).toLocaleDateString()}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                disabled={isStreaming}
                size="small"
                onClick={handleDownloadIsolationChecklist}
                title="Download as PDF">
                <DownloadRounded />
              </IconButton>
              <IconButton onClick={() => setExpanded(!expanded)} size="small">
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Steps Table */}
        <Collapse in={expanded} timeout="auto">
          <CardContent sx={{ p: 0 }}>
            {steps.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Step
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Description
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Line Reference
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="center">
                        <Typography variant="subtitle2" fontWeight={600}>
                          Action
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Purpose
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Responsibility
                        </Typography>
                      </TableCell>
                      {/* <TableCell
                        className={classes.tableCell}
                        align="center"></TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {steps.map((step, index) => (
                      <TableRow
                        key={index}
                        className={classes.stepRow}
                        sx={{
                          backgroundColor:
                            selectedIsolationMarkup?.number === step.number
                              ? theme.palette.action.selected
                              : "initial",
                        }}
                        onClick={() => handleClickStep(step)}>
                        <TableCell className={classes.tableCell}>
                          <Typography variant="body2" fontWeight={600}>
                            {step.number}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Tooltip title={step.description}>
                            <Typography
                              variant="body2"
                              className={classes.truncateText}>
                              {step.description}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Typography
                            variant="caption"
                            className={classes.truncateText}
                            sx={{
                              background:
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "rgba(0, 0, 0, 0.04)",
                              padding: "4px 8px",
                              borderRadius: 1,
                              display: "inline-block",
                            }}>
                            {step.lineRef}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="center">
                          <Chip
                            label={step.action}
                            size="small"
                            className={classes.actionChip}
                          />
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Tooltip title={step.purpose}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              className={classes.truncateText}>
                              {step.purpose}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className={classes.truncateText}>
                            {step.responsibility}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box p={3} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  No isolation steps available
                </Typography>
              </Box>
            )}

            {/* Footer with PID link if available */}
            {attributes.pid_url && (
              <>
                <Divider />
                <Box p={2} display="flex" justifyContent="flex-end">
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      onIsolationMarkupChange({
                        pid_url: attributes.pid_url,
                        annotations: steps,
                      });
                      if (steps.length > 0) {
                        onSelectedIsolationMarkupChange(
                          steps[steps.length - 1]
                        );
                      }
                    }}
                    sx={{ textTransform: "none" }}>
                    Apply Markup
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Collapse>
      </Card>
      {openPIDMarkup && (
        <PIDMarkup
          pid_url={attributes.pid_url}
          annotations={steps}
          onClose={handleClosePIDMarkup}
        />
      )}
    </Box>
  );
}

export default IsolationChecklistBlock;
